import { Injectable } from '@angular/core';

declare var rrule: any;

@Injectable({
  providedIn: 'root',
})
export class RruleService {
  /**
   * List of month days for recurring events, populated in the constructor
   */
  monthDays: Array<Array<number>> | undefined;

  /**
   * List of recurring types
   * @type {Array<any>}
   */
  repeats: Array<any> | undefined;

  /**
   * List of rrule frequencies with array index as their rrule js value
   * @type {Array<string>}
   */
  rruleFrequencies: Array<string> = ['YEARLY', 'MONTHLY', 'WEEKLY', 'DAILY'];

  rruleWeekdays: Array<string> = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

  /**
   * List of week days for recurring events
   * @type {Array<any>}
   */
  weekDays: Array<any> | undefined;

  /**
   * Constructor function
   */
  constructor() {}

  /**
   * Build recurring rule string
   * @param {any = {}} recurring     Object with keys/values for rrule library to build recurring rule string
   * @param {any = {}} recurringDays Object with selected days/weekdays, etc. for weekly or monthly rules to be processed into recurring object before building rrule
   */
  buildRrule(recurring: any = {}, recurringDays: any = {}): any {
    if (!recurring.freq || recurring.freq == 'NONE') {
      return {};
    }
    let options: any = {
      freq: rrule.RRule[recurring.freq],
      interval: recurring.interval || 1,
    };
    if (recurring.freq == 'MONTHLY' && (!recurringDays.monthType || recurringDays.monthType == 'day_of_month') && recurringDays.by_month_day && Object.keys(recurringDays.by_month_day).length > 0) {
      options.bymonthday = [];
      for (let monthDay in recurringDays.by_month_day) if (recurringDays.by_month_day.hasOwnProperty(monthDay) && recurringDays.by_month_day[monthDay]) options.bymonthday.push(monthDay);
    } else if (recurring.freq == 'MONTHLY' && recurringDays.monthType == 'day_of_week' && recurringDays.by_day && Object.keys(recurringDays.by_day).length > 0) {
      options.byweekday = [];
      for (let weekDay in recurringDays.by_day)
        if (recurringDays.by_day.hasOwnProperty(weekDay) && recurringDays.by_day[weekDay]) {
          let week: number = Number(weekDay.split('_')[0]);
          options.byweekday.push(rrule.RRule[weekDay.split('_')[1]].nth(weekDay.split('_')[0]));
        }
    } else if (recurring.freq == 'WEEKLY' && recurringDays.by_week_day && Object.keys(recurringDays.by_week_day).length > 0) {
      options.byweekday = [];
      for (let weekDay in recurringDays.by_week_day) if (recurringDays.by_week_day.hasOwnProperty(weekDay) && recurringDays.by_week_day[weekDay]) options.byweekday.push(rrule.RRule[weekDay]);
    }
    let rule: any = new rrule.RRule(options);
    return {
      string: rule.toString(),
      text: rule.toText(),
    };
  }

  /**
   * Convert recurring rule to an object
   * @param {string} rule Recurring rule string
   */
  buildRruleObjects(rule: string) {
    rule = String(rule).trim();
    if (rule[rule.length - 1] == ';') rule = rule.slice(0, -1);
    let rruleObject: any = rrule.RRule.parseString(rule);
    let recurring: any = {
      freq: 'NONE',
      interval: 1,
    };
    let recurringDays: any = {
      by_day: {},
      by_month_day: {},
      by_week_day: {},
    };
    if (rruleObject.freq || rruleObject.freq === 0) {
      recurring.freq = this.rruleFrequencies[rruleObject.freq];
      if (!recurring.freq) recurring.freq = 'NONE';
    }
    if (rruleObject.interval) recurring.interval = rruleObject.interval;
    if (rruleObject.bymonthday) for (let monthDay of rruleObject.bymonthday) recurringDays.by_month_day[monthDay] = true;
    if (rruleObject.byweekday || rruleObject.byweekday === 0)
      for (let weekDayObject of rruleObject.byweekday) {
        let weekDay: string = this.rruleWeekdays[weekDayObject.weekday];
        if (weekDayObject.n || weekDayObject.n === 0) {
          weekDay = `${weekDayObject.n}_${weekDay}`;
          recurringDays.by_day[weekDay] = true;
        } else recurringDays.by_week_day[weekDay] = true;
      }
    return {
      recurring: recurring,
      recurringDays: recurringDays,
      rrule_text: rrule.RRule.fromString(rule).toText(),
    };
  }

  /**
   * List of month days
   * @return {Array} List of month days
   */
  getMonthDays(): Array<Array<number>> {
    let monthDays: Array<Array<number>> = [];
    let array: Array<number> = [];
    for (let i = 1; i <= 31; i += 1) {
      array.push(i);
      if (i % 7 == 0 && i > 1) {
        monthDays.push(array);
        array = [];
      }
    }
    monthDays.push(array);
    return monthDays;
  }

  /**
   * List of rrule types
   * @return {Array<any>} List of rrule types
   */
  getRepeats(): Array<any> {
    return [
      { label: 'None', value: 'NONE' },
      { label: 'Daily', value: 'DAILY', rrule_value: 3 },
      { label: 'Weekly', value: 'WEEKLY', rrule_value: 2 },
      { label: 'Monthly', value: 'MONTHLY', rrule_value: 1 },
      { label: 'Yearly', value: 'YEARLY', rrule_value: 0 },
    ];
  }

  /**
   * List of week days
   * @return {Array<any>} List of week days
   */
  getWeekDays(): Array<any> {
    return [
      { label: 'S', value: 'SU', rrule_value: 6 },
      { label: 'M', value: 'MO', rrule_value: 0 },
      { label: 'T', value: 'TU', rrule_value: 1 },
      { label: 'W', value: 'WE', rrule_value: 2 },
      { label: 'T', value: 'TH', rrule_value: 3 },
      { label: 'F', value: 'FR', rrule_value: 4 },
      { label: 'S', value: 'SA', rrule_value: 5 },
    ];
  }

  /**
   * Clear variables
   */
  clearVariables() {
    this.monthDays = undefined;
    this.repeats = undefined;
    this.weekDays = undefined;
  }

  /**
   * Set list of month days into variable
   */
  setMonthDays() {
    if (!this.monthDays) this.monthDays = this.getMonthDays();
  }

  /**
   * Set list of rrule types
   */
  setRepeats() {
    if (!this.repeats) this.repeats = this.getRepeats();
  }

  /**
   * Set default variables required for rrule value selection
   */
  setVariables() {
    this.setMonthDays();
    this.setRepeats();
    this.setWeekDays();
  }

  /**
   * Set list of weekdays into variable
   */
  setWeekDays() {
    if (!this.weekDays) this.weekDays = this.getWeekDays();
  }
}
